import { useState } from "react";
import { api, getToken, type Participant } from "../lib/api";
import { useFetch } from "../lib/useFetch";
import FeedbackSection from "../components/FeedbackSection";

interface Incident {
  id: number; occurredAt: string; location: string | null; category: string;
  severity: string; description: string; immediateActions: string | null;
  reportableToNdis: boolean; status: string; reporterName: string; participantName: string | null;
}
interface Complaint {
  id: number; reference: string; category: string; details: string;
  complainantName: string | null; status: string; createdAt: string;
}
interface Action { id: number; description: string; assigneeName: string | null; dueDate: string | null; status: string }
interface Worker { id: number; name: string }
interface RestrictivePractice {
  id: number; type: string; authorisation: string; bspLinked: boolean;
  startDate: string; reviewDate: string; notes: string | null; participantName: string;
}

const RP_TYPES = ["chemical", "physical", "mechanical", "environmental", "seclusion"];

const field = "input";
const sevStyle: Record<string, string> = {
  minor: "bg-slate-100 text-body", moderate: "bg-amber-50 text-amber-700", severe: "bg-red-100 text-red-700",
};
const statusStyle: Record<string, string> = {
  open: "bg-amber-50 text-amber-700", in_review: "bg-sky-50 text-sky-700", closed: "bg-slate-100 text-faint",
  received: "bg-amber-50 text-amber-700", investigating: "bg-sky-50 text-sky-700", resolved: "bg-teal-50 text-teal-700",
};

export default function Quality() {
  const incidents = useFetch<Incident[]>("/v1/incidents");
  const complaints = useFetch<Complaint[]>("/v1/complaints");
  const workers = useFetch<Worker[]>("/v1/rostering/workers");
  const [showIncident, setShowIncident] = useState(false);
  const [openActions, setOpenActions] = useState<number | null>(null);
  const [inc, setInc] = useState({ occurredAt: "", category: "other", severity: "minor", description: "", location: "", immediateActions: "" });
  const [error, setError] = useState<string | null>(null);

  async function reportIncident(e: React.FormEvent) {
    e.preventDefault();
    try {
      await api("/v1/incidents", { method: "POST", body: JSON.stringify({
        occurredAt: inc.occurredAt, category: inc.category, severity: inc.severity,
        description: inc.description, location: inc.location || undefined,
        immediateActions: inc.immediateActions || undefined,
      }) });
      setShowIncident(false); setError(null);
      setInc({ occurredAt: "", category: "other", severity: "minor", description: "", location: "", immediateActions: "" });
      incidents.reload();
    } catch (err) { setError((err as Error).message); }
  }
  async function setIncidentStatus(id: number, status: string) {
    await api(`/v1/incidents/${id}/status`, { method: "POST", body: JSON.stringify({ status }) });
    incidents.reload();
  }
  async function setComplaintStatus(id: number, status: string) {
    await api(`/v1/complaints/${id}/status`, { method: "POST", body: JSON.stringify({ status }) });
    complaints.reload();
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-ink">Quality & safeguarding</h1>
        <p className="text-sm text-mut">Incident and complaint registers — NDIS Practice Standards audit evidence.</p>
      </div>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-ink">Incident register</h2>
          <button onClick={() => setShowIncident((v) => !v)}
            className="btn-primary">
            {showIncident ? "Cancel" : "Report incident"}
          </button>
        </div>
        {showIncident && (
          <form onSubmit={reportIncident} className="card p-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
            <label className="block"><span className="label">When</span>
              <input type="datetime-local" value={inc.occurredAt} onChange={(e) => setInc({ ...inc, occurredAt: e.target.value.slice(0, 16) })} className={field} /></label>
            <label className="block"><span className="label">Category</span>
              <select value={inc.category} onChange={(e) => setInc({ ...inc, category: e.target.value })} className={field}>
                {["fall", "medication_error", "behavioural", "injury", "property", "other"].map((x) => <option key={x} value={x}>{x.replace("_", " ")}</option>)}
              </select></label>
            <label className="block"><span className="label">Severity</span>
              <select value={inc.severity} onChange={(e) => setInc({ ...inc, severity: e.target.value })} className={field}>
                <option value="minor">Minor</option><option value="moderate">Moderate</option><option value="severe">Severe</option>
              </select></label>
            <label className="block col-span-full"><span className="label">What happened</span>
              <textarea rows={3} value={inc.description} onChange={(e) => setInc({ ...inc, description: e.target.value })} className={field} /></label>
            <label className="block col-span-full sm:col-span-2"><span className="label">Immediate actions taken</span>
              <input value={inc.immediateActions} onChange={(e) => setInc({ ...inc, immediateActions: e.target.value })} className={field} /></label>
            <label className="block"><span className="label">Location</span>
              <input value={inc.location} onChange={(e) => setInc({ ...inc, location: e.target.value })} className={field} /></label>
            <div className="col-span-full">
              <button className="btn-dark">Submit report</button>
              {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
            </div>
          </form>
        )}
        {incidents.error && <p className="text-sm text-faint card p-5">The register is visible to coordinators and owners. Your reports are received.</p>}
        {incidents.data?.length === 0 && <p className="text-sm text-faint card p-5">No incidents recorded.</p>}
        {incidents.data?.map((i) => (
          <div key={i.id} className="card p-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`chip ${sevStyle[i.severity]}`}>{i.severity}</span>
              <span className="text-sm font-medium text-ink capitalize">{i.category.replace("_", " ")}</span>
              <span className="text-xs text-faint font-mono">{i.occurredAt.replace("T", " ")}</span>
              {i.reportableToNdis && <span className="chip bg-red-50 text-red-700">NDIS reportable — review</span>}
              <span className={`chip ${statusStyle[i.status]}`}>{i.status.replace("_", " ")}</span>
              <span className="ml-auto flex gap-2">
                <button onClick={() => setOpenActions(openActions === i.id ? null : i.id)} className="btn-secondary px-2.5 py-1 text-xs">
                  {openActions === i.id ? "Hide actions" : "Actions"}
                </button>
                {i.status === "open" && <button onClick={() => setIncidentStatus(i.id, "in_review")} className="btn-secondary px-2.5 py-1 text-xs">Start review</button>}
                {i.status !== "closed" && <button onClick={() => setIncidentStatus(i.id, "closed")} className="btn-secondary px-2.5 py-1 text-xs">Close</button>}
              </span>
            </div>
            <p className="text-sm text-body mt-2">{i.description}</p>
            {i.immediateActions && <p className="text-xs text-mut mt-1">Immediate actions: {i.immediateActions}</p>}
            <p className="text-xs text-faint mt-1">Reported by {i.reporterName}{i.location ? ` · ${i.location}` : ""}{i.participantName ? ` · ${i.participantName}` : ""}</p>
            {openActions === i.id && <IncidentActions incidentId={i.id} workers={workers.data ?? []} />}
          </div>
        ))}
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-medium text-ink">Complaints register</h2>
        {complaints.data?.length === 0 && <p className="text-sm text-faint card p-5">No complaints recorded.</p>}
        {complaints.data?.map((cm) => (
          <div key={cm.id} className="card p-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-mut">{cm.reference}</span>
              <span className="text-sm font-medium text-ink capitalize">{cm.category.replace("_", " ")}</span>
              <span className={`chip ${statusStyle[cm.status] ?? "bg-slate-100 text-mut"}`}>{cm.status}</span>
              <span className="text-xs text-faint">{cm.complainantName ?? "Anonymous"}</span>
              <span className="ml-auto flex gap-2">
                {cm.status === "received" && <button onClick={() => setComplaintStatus(cm.id, "investigating")} className="btn-secondary px-2.5 py-1 text-xs">Investigate</button>}
                {cm.status === "investigating" && <button onClick={() => setComplaintStatus(cm.id, "resolved")} className="btn-secondary px-2.5 py-1 text-xs">Resolve</button>}
                {cm.status !== "closed" && <button onClick={() => setComplaintStatus(cm.id, "closed")} className="btn-secondary px-2.5 py-1 text-xs">Close</button>}
              </span>
            </div>
            <p className="text-sm text-body mt-2">{cm.details}</p>
          </div>
        ))}
      </section>

      <RestrictivePractices />
      <FeedbackSection />
    </div>
  );
}

function IncidentActions({ incidentId, workers }: { incidentId: number; workers: Worker[] }) {
  const { data, reload } = useFetch<Action[]>(`/v1/incidents/${incidentId}/actions`);
  const [form, setForm] = useState({ description: "", assigneeId: "", dueDate: "" });

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!form.description.trim()) return;
    await api(`/v1/incidents/${incidentId}/actions`, { method: "POST", body: JSON.stringify({
      description: form.description.trim(),
      assigneeId: form.assigneeId ? Number(form.assigneeId) : undefined,
      dueDate: form.dueDate || undefined,
    }) });
    setForm({ description: "", assigneeId: "", dueDate: "" }); reload();
  }
  async function complete(id: number) {
    await api(`/v1/actions/${id}/complete`, { method: "POST", body: "{}" });
    reload();
  }

  return (
    <div className="mt-3 border-t border-edge-soft pt-3 space-y-2">
      {(data ?? []).map((a) => (
        <div key={a.id} className="flex items-center gap-2 text-sm">
          <span className={`chip ${a.status === "done" ? "bg-teal-50 text-teal-700" : "bg-amber-50 text-amber-700"}`}>{a.status}</span>
          <span className="text-body">{a.description}</span>
          <span className="text-xs text-faint">{a.assigneeName ?? "unassigned"}{a.dueDate ? ` · due ${a.dueDate}` : ""}</span>
          {a.status !== "done" && <button onClick={() => complete(a.id)} className="ml-auto text-xs text-teal-700 hover:underline">Mark done</button>}
        </div>
      ))}
      {(data ?? []).length === 0 && <p className="text-xs text-faint">No follow-up actions. Closure requires all actions complete.</p>}
      <form onSubmit={add} className="flex flex-wrap items-center gap-2 pt-1">
        <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Follow-up action…" className="input mt-0 flex-1 min-w-40 text-sm" />
        <select value={form.assigneeId} onChange={(e) => setForm({ ...form, assigneeId: e.target.value })} className="input mt-0 w-40 text-sm">
          <option value="">Assign to…</option>
          {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <input type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} className="input mt-0 w-40 text-sm" />
        <button className="btn-dark text-sm">Add</button>
      </form>
    </div>
  );
}

function RestrictivePractices() {
  const { data, reload } = useFetch<RestrictivePractice[]>("/v1/restrictive-practices");
  const participants = useFetch<Participant[]>("/v1/participants");
  const [form, setForm] = useState({ participantId: "", type: "environmental", authorisation: "", bspLinked: false, startDate: "", reviewDate: "" });
  const [error, setError] = useState<string | null>(null);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await api("/v1/restrictive-practices", { method: "POST", body: JSON.stringify({
        participantId: Number(form.participantId), type: form.type, authorisation: form.authorisation,
        bspLinked: form.bspLinked, startDate: form.startDate, reviewDate: form.reviewDate,
      }) });
      setForm({ participantId: "", type: "environmental", authorisation: "", bspLinked: false, startDate: "", reviewDate: "" });
      reload();
    } catch (err) { setError((err as Error).message); }
  }
  async function exportCsv() {
    const res = await fetch(`${import.meta.env.VITE_API_URL ?? ""}/v1/reports/restrictive-practices.csv`, {
      headers: { authorization: `Bearer ${getToken()}` },
    });
    if (!res.ok) { setError(`Export not available (${res.status})`); return; }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url; a.download = "restrictive-practices-register.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium text-ink">Restrictive practices register</h2>
        <button onClick={exportCsv} className="text-sm text-teal-700 hover:underline">Export CSV ↓</button>
      </div>
      <form onSubmit={add} className="card p-4 grid grid-cols-2 sm:grid-cols-6 gap-3 items-end">
        <label className="block"><span className="label">Participant</span>
          <select value={form.participantId} onChange={(e) => setForm({ ...form, participantId: e.target.value })} className="input" required>
            <option value="">Select…</option>
            {(participants.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>)}
          </select></label>
        <label className="block"><span className="label">Type</span>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input">
            {RP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select></label>
        <label className="block sm:col-span-2"><span className="label">Authorisation</span>
          <input value={form.authorisation} onChange={(e) => setForm({ ...form, authorisation: e.target.value })} className="input" required /></label>
        <label className="block"><span className="label">Start</span>
          <input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} className="input" required /></label>
        <label className="block"><span className="label">Review</span>
          <input type="date" value={form.reviewDate} onChange={(e) => setForm({ ...form, reviewDate: e.target.value })} className="input" required /></label>
        <label className="flex items-center gap-2 text-sm text-body sm:col-span-2">
          <input type="checkbox" checked={form.bspLinked} onChange={(e) => setForm({ ...form, bspLinked: e.target.checked })} />
          Linked to behaviour support plan
        </label>
        <button className="btn-dark">Record</button>
        {error && <p className="text-sm text-red-600 col-span-full">{error}</p>}
      </form>
      {data?.length === 0 && <p className="text-sm text-faint card p-5">No restrictive practices recorded.</p>}
      {(data ?? []).map((r) => (
        <div key={r.id} className="card p-4 flex flex-wrap items-center gap-2">
          <span className="chip bg-red-50 text-red-700 capitalize">{r.type}</span>
          <span className="text-sm font-medium text-ink">{r.participantName}</span>
          <span className="text-xs text-mut">auth: {r.authorisation}</span>
          {r.bspLinked && <span className="chip bg-teal-50 text-teal-700">BSP-linked</span>}
          <span className="text-xs text-faint ml-auto">review {r.reviewDate}</span>
        </div>
      ))}
    </section>
  );
}
